import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api/client';

/**
 * Every POS lane on the platform, with the newest build any of them has
 * reported.
 *
 * `pos/all` is the fleet view — the store-scoped `/pos` page manages one
 * store's lanes, this answers "which tills are behind". The version comparison
 * is the whole point of the endpoint: `LatestVersion` comes back alongside the
 * lanes so a client can tell current from stale without knowing what current is.
 */

export interface PosLane {
  id: string;
  businessId: string | null;
  businessName: string | null;
  tag: string | null;
  createdAt: string;
  isBlocked: boolean;
  lastReportedVersion: string | null;
  lastReportedDeviceManufacturer: string | null;
  lastReportedDeviceModel: string | null;
  lastReportedDeviceName: string | null;
  lastReportedTerminalId: string | null;
  lastTransactionOn: string | null;
}

export interface PosLanes {
  latestVersion: string | null;
  lanes: PosLane[] | null;
}

export type VersionState = 'current' | 'behind' | 'unknown';

export function versionStateOf(
  lane: PosLane,
  latestVersion: string | null
): VersionState {
  if (!lane.lastReportedVersion) return 'unknown';
  if (!latestVersion) return 'unknown';
  return lane.lastReportedVersion === latestVersion ? 'current' : 'behind';
}

export function useAllPosLanes() {
  return useQuery({
    queryKey: ['pos', 'all'],
    queryFn: () => get<PosLanes>('pos/all'),
    staleTime: 30_000,
  });
}
